import { useRef, useState } from 'react';
import { PromptGroup } from '@server/prompts';
import { Stack } from '@mui/material';
import { PromptCard } from '../../promptsLibrary__deprecated/promptGroupCard/PromptCard';
import { PromptsLibraryDialog } from '../../promptsLibrary__deprecated/PromptsLibraryDialog';
import { PromptsLibraryV2Dialog } from '../PromptsLibraryV2Dialog';
import { PromptGroupMinimizedPopover } from './PromptGroupMinimizedPopover';
import { selectPromptGroups } from '../../../../reducers/promptLibraryReducer';
import { useSelector } from 'react-redux';
import { AI_AGENTS_EVENTS } from '../../../../services/events';
import useSendBiEvent from '../../../../hooks/useSendBiEvent';
import { useFeatureToggle } from '../../../../feature-flags/useFeatureToggle';

interface PromptGroupsMinimizedProps {
  onPromptClick: (promptTitle: string) => Promise<void>;
}

export const PromptGroupsMinimized = ({ onPromptClick }: PromptGroupsMinimizedProps) => {
  const allPromptGroups = useSelector(selectPromptGroups);
  const [openPromptsLibraryDialog, setOpenPromptsLibraryDialog] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<PromptGroup | null>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const sendBiEvent = useSendBiEvent();
  const { promptsLibraryV2 } = useFeatureToggle();

  const minimizedPromptGroups =
    allPromptGroups?.filter((promptGroup) => promptGroup.isPinned) ?? [];

  const renderPromptsLibraryDialog = () => {
    if (!openPromptsLibraryDialog) return null;

    if (promptsLibraryV2) {
      return (
        <PromptsLibraryV2Dialog
          open={openPromptsLibraryDialog}
          onClose={() => setOpenPromptsLibraryDialog(false)}
        />
      );
    }

    return (
      <PromptsLibraryDialog
        open={openPromptsLibraryDialog}
        onClose={() => setOpenPromptsLibraryDialog(false)}
        onPromptClick={onPromptClick}
      />
    );
  };

  return (
    <>
      {renderPromptsLibraryDialog()}

      {selectedGroup && (
        <PromptGroupMinimizedPopover
          prompt={selectedGroup}
          onClose={() => setSelectedGroup(null)}
          onPromptClick={async (promptText: string) => {
            sendBiEvent({ name: AI_AGENTS_EVENTS.promptsLibraryPinnedPromptClick });
            await onPromptClick(promptText);
          }}
          anchorEl={parentRef.current}
        />
      )}

      <Stack
        direction="row"
        gap={1}
        flexWrap="wrap"
        justifyContent="center"
        alignItems="center"
        ref={parentRef}
      >
        {minimizedPromptGroups.map((prompt) => (
          <PromptCard
            key={prompt.id}
            promptGroup={prompt}
            onClick={() => {
              sendBiEvent({ name: AI_AGENTS_EVENTS.promptsLibraryPinnedPromptCategoryClick });
              setSelectedGroup(prompt);
            }}
            isMinimized={true}
          />
        ))}

        {/* by product definition: always show 'more' to allow access to library */}
        <PromptCard
          key="more"
          promptGroup={{
            id: 'more',
            name: 'Prompts Library',
            description: '',
            iconName: 'Plus',
            prompts: [],
            isPinned: true,
            accountId: '',
            createdAt: '',
            updatedAt: '',
          }}
          onClick={() => {
            sendBiEvent({ name: AI_AGENTS_EVENTS.promptsLibraryOpen });
            setOpenPromptsLibraryDialog(true);
          }}
          isMinimized={true}
        />
      </Stack>
    </>
  );
};
